/* <facet-filters>: auto-submits the shared facet form on change so the Apply
   click disappears for JS users. Full-page reload keeps native behavior as
   the single source of truth; the Apply button remains the no-JS path.
   Open filter panels are absolute dropdowns, so they dismiss on outside
   pointer interaction, page scroll, and Escape; opening one closes others.
   On mobile the groups render as a horizontally-scrolling chip row, which
   clips absolute children — there the open panel is pinned under the row
   with fixed positioning (recomputed on open; panels close on scroll/resize). */
(() => {
  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-facet-form]');
      if (!this.form) return;
      this.classList.add('is-enhanced');
      this.timer = null;

      this.form.addEventListener('change', (event) => {
        if (event.target.matches('input[type="checkbox"]')) {
          this.submit();
        } else if (event.target.matches('[data-facet-price]')) {
          window.clearTimeout(this.timer);
          this.timer = window.setTimeout(() => this.submit(), 600);
        }
      });

      // toggle doesn't bubble; capture it to run one-open-at-a-time
      this.addEventListener(
        'toggle',
        (event) => {
          if (event.target.matches('details[open]')) {
            this.querySelectorAll('details[open]').forEach((panel) => {
              if (panel !== event.target) panel.removeAttribute('open');
            });
            this.positionPanel(event.target);
          }
        },
        true
      );

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.querySelector('details[open]');
        if (open) {
          open.removeAttribute('open');
          open.querySelector('summary')?.focus();
        }
      });

      this.onOutsidePointer = (event) => {
        if (!this.contains(event.target)) this.closePanels();
      };
      this.onPageScroll = () => this.closePanels();
      this.onViewportResize = () => this.closePanels();
      document.addEventListener('pointerdown', this.onOutsidePointer);
      window.addEventListener('scroll', this.onPageScroll, { passive: true });
      window.addEventListener('resize', this.onViewportResize);
    }

    disconnectedCallback() {
      document.removeEventListener('pointerdown', this.onOutsidePointer);
      window.removeEventListener('scroll', this.onPageScroll);
      window.removeEventListener('resize', this.onViewportResize);
    }

    // The mobile chip row scrolls horizontally and therefore clips absolutely
    // positioned children, so pin the open panel under the row instead.
    positionPanel(details) {
      const panel = details.querySelector('.collection-filter__panel');
      if (!panel) return;
      if (!window.matchMedia('(max-width: 600px)').matches) {
        panel.style.top = panel.style.left = panel.style.width = '';
        return;
      }
      const row = this.querySelector('.collection-filters__groups') || details;
      const rect = row.getBoundingClientRect();
      panel.style.top = `${Math.round(rect.bottom + 8)}px`;
      panel.style.left = `${Math.round(rect.left)}px`;
      panel.style.width = `${Math.round(rect.width)}px`;
    }

    closePanels() {
      this.querySelectorAll('details[open]').forEach((details) => {
        details.removeAttribute('open');
        const panel = details.querySelector('.collection-filter__panel');
        if (panel) panel.style.top = panel.style.left = panel.style.width = '';
      });
    }

    submit() {
      window.clearTimeout(this.timer);
      this.form.requestSubmit();
    }
  }

  if (!window.customElements.get('facet-filters')) {
    window.customElements.define('facet-filters', FacetFilters);
  }

  // Sort selects live outside the facet form; auto-submit and retire their
  // Apply button wherever they appear (collections, search).
  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-facet-sort]');
    if (!sort) return;
    // Heat sorts are applied in place by assets/heat-sort.js. Shopify does not
    // know the value, so submitting would reload to the DEFAULT order while the
    // select still read "Heat, high to low".
    if (sort.value.startsWith('heat-')) return;
    sort.form?.requestSubmit();
  });
  window.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-facet-sort-apply]').forEach((btn) => {
      btn.setAttribute('hidden', '');
    });
  });
})();
